import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  Card,
  Row,
  Col,
  Table,
} from "antd";
import {
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import PageTitle from "../PageTitle";
import { useParams } from "react-router-dom";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import { toast } from "react-toastify";
import { getNoticeDetails, postData } from "../../api/apiService";
import axios from "axios";
import FileUploadModal from "../FileUploadModal";
import { FILE_PREFIX, getFileUrl } from "../../utils/constants";
import { getEmbedToken } from "../../api/api";

const { Option } = Select;

const AddResponseOutstandingsForm = ({ serviceType }) => {
  const [form] = Form.useForm();
  const { clientId } = useParams();
  const navigate = useTenantNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [noticeLetter, setNoticeLetter] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    console.log("Form Values:", values);

    if (!clientDetails?.id) {
      toast.error("Client details not loaded yet. Please wait a moment and try again.");
      return;
    }

    const payload = {
      ...values,
      // antd DatePicker passes a moment object via `values` — format to ISO
      // before sending so the backend can parse it with date.fromisoformat.
      date_of_service_notice: values.date_of_service_notice
        ? values.date_of_service_notice.format("YYYY-MM-DD")
        : null,
      // notice_letter is stored in component state (not antd form internal
      // store) because the Form.Item child is a <div>/<Button> not a form
      // control — same reason as AddEproceedingForm (#78a). Pulling from
      // state directly here is the only reliable path.
      notice_letter: noticeLetter || null,
      client: clientDetails.id,
    };

    try {
      setLoading(true);

      // Backend create endpoint added in #84 (mirror of #77's create_e_proceeding).
      // Replaces the old `createNotice("Response to Outstanding Demand", payload)`
      // call which POSTed to `/Response to Outstanding Demand` (Frappe REST style)
      // — that path returned 404.
      await postData("/api/v1/income-tax/demands", payload);

      toast.info("Notice Added Successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      setTimeout(() => {
        navigate(
          `/income-tax/responseoutstandings/${clientId}?noticeType=active`
        );
      }, 500);
    } catch (err) {
      toast.error("Error in Creating notice ", {
        position: "top-right",
        autoClose: 5000,
      });
      console.log("Error", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleUpload = async (file) => {
    try {
      setUploadLoading(true);

      const payload = new FormData();
      payload.append("is_private", "0");
      // payload.append("doctype", "E Proceeding");
      // payload.append("docname", clientId);
      payload.append("file", file, file.name);

      const token = getEmbedToken() || localStorage.getItem("authToken") || "";
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/api/v1/income-tax/upload",
        payload,
        config
      );

      const fileURL = response?.data?.file_url;

      if (fileURL) {
        // Store as `{file_url, file_name}` dict so the original filename
        // can be persisted to the backend's notice_letter_file_name column
        // (added in #80). Backend create endpoint accepts both shapes.
        const fileObject = { file_url: fileURL, file_name: file.name };
        form.setFieldsValue({ notice_letter: fileObject });
        setNoticeLetter(fileObject);
      }

      toast.info("File uploaded successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      handleModalClose();
    } catch (error) {
      toast.error("Error uploading file", {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Error:", error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (!clientId) return;

    // Use the migrated GET endpoint instead of the legacy POST to
    // `/clients/{type}/details` (which doesn't exist on the FastAPI
    // backend — same bug fixed in #75 for EPForm.jsx and #77 for
    // AddEproceedingForm).
    const basePathMap = {
      gstin: "gst",
      "income-tax": "income-tax",
      tds: "tds",
    };
    const basePath = basePathMap[serviceType] || "income-tax";
    const endpoint = `/api/v1/clients/${basePath}/${clientId}`;

    getNoticeDetails(endpoint)
      .then((data) => {
        // Backend returns `disabled` as a boolean. Normalize to number for
        // consistency with legacy comparison patterns elsewhere.
        const normalized = data
          ? { ...data, disabled: data.disabled ? 1 : 0 }
          : { disabled: 0 };
        setClientDetails(normalized);
      })
      .catch((err) => {
        console.error("Failed to fetch client details:", err);
        setClientDetails({ disabled: 0 });
      });
  }, [clientId, serviceType]);

  useEffect(() => {
    if (clientDetails) {
      form.setFieldValue("client", clientDetails?.client_name);
    }
  }, [clientDetails]);

  return (
    <div>
      <PageTitle title="New Response To Outstanding Demand" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-100 p-4">
        <div className="w-full max-w-5xl bg-white shadow-lg rounded-2xl p-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="w-full"
          >
            <Card
              title="Notice Details"
              bordered={false}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Demand Reference No"
                    name="demand_reference_no"
                    rules={[
                      {
                        required: true,
                        message: "Please enter demand reference number",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Assessment Year" name="assessment_year">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Section Code" name="section_code">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Client" name="client">
                    <Input disabled />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Rectification Rights"
                    name="rectification_rights"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Mode of Service" name="mode_of_service">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Response Type" name="response_type">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Date of Service Notice" name="date_of_service_notice" rules={[{ required: true, message: "Please select a due date" }]}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Outstanding Demand Amount"
                    name="outstanding_demand_amount"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Notice" name="notice_letter">
                    {noticeLetter ? (
                      <div className="flex items-center space-x-2">
                        <a
                          href={getFileUrl(noticeLetter?.file_url || noticeLetter)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            icon={<LinkOutlined />}
                            style={{ padding: "20px" }}
                          >
                            {" "}
                            <span
                              style={{
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {noticeLetter?.file_name ||
                                (typeof noticeLetter === "string"
                                  ? noticeLetter.split("/").pop()
                                  : "")}
                            </span>
                          </Button>
                        </a>

                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setNoticeLetter(null);
                            form.setFieldsValue({ notice_letter: "" });
                          }}
                        />
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setIsModalVisible(true);
                        }}
                        icon={<UploadOutlined />}
                      >
                        Attach
                      </Button>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <div className="flex justify-end mt-4">
              <Button type="primary" loading={loading} htmlType="submit">
                {loading ? "Adding.." : " Add Notice"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <FileUploadModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onUpload={handleUpload}
        disabled={uploadLoading}
      />
    </div>
  );
};

export default AddResponseOutstandingsForm;
